import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Checkbox from '../../../components/atoms/forms/Checkbox';
import StopBadge from '../../../components/atoms/StopBadge';
import StopStatusPill from '../../../components/atoms/StopStatusPill';
import {
  STATUS_IDS,
  STATUS_META,
  isActionableStatus,
} from '../../../libs/stopStatus';
import { parseUtcDateTime } from '../../../utils/datetime';
import { formatClockTime } from '../../../utils/displayFormat';

// One stop row inside a RouteCard. Owns no edit state — fields are
// controlled by the parent useRouteEdits hook. Drag handle, checkbox, time
// cells, and per-row Delete action are disabled for stops the driver has
// already engaged with (status derived from driver-action events).
//
// The window cells are read-only display; clicking them (or dragging the
// row) opens PinStopModal via the parent.

export function isStopActionable(stop) {
  if (!stop) return false;
  // Driver-action-derived status is the authoritative signal — only
  // NOT_STARTED rows can be edited. Everything else (VAN_TEMPERATURE_SET,
  // IN_PROGRESS, COMPLETED, …) is locked because the driver has touched
  // the stop. See `libs/stopStatus.js` for the precedence rules.
  const derivedStatusId = stop.derivedStatus && stop.derivedStatus.status;
  if (derivedStatusId) {
    return isActionableStatus(derivedStatusId);
  }
  // Fallback for consumers that don't supply a derived status (e.g., first
  // render before GetDriverAction resolves). `stockedTimeUTC` on its own
  // isn't reliable — the BE keeps the column populated on historical rows
  // even after the driver-action event has fallen outside the active
  // window — so we only consult it when nothing better is available.
  if (stop.stockedTimeUTC) return false;
  return true;
}

export function formatStockedTime(iso) {
  // stockedTimeUTC is "YYYY-MM-DD HH:MM:SS" UTC with no zone suffix —
  // parseUtcDateTime pins it to UTC before converting to browser-local.
  return formatClockTime(parseUtcDateTime(iso));
}

// Columns: checkbox, drag, badge, location, totes, status, delivery window,
// action. Location uses `minmax(0, 1fr)` so it absorbs extra width on
// wide screens without contributing per-row variance to the row's
// intrinsic size — see the matching comment in StopList.js. Keep in
// sync with StopList's GRID_COLUMNS.
const GRID_COLUMNS = '32px 24px 40px minmax(0, 1fr) 80px 150px 260px 60px';

const StyledDiv = styledComponent('div', {
  display: 'grid',
  gridTemplateColumns: GRID_COLUMNS,
  alignItems: 'center',
  gap: theme.spacing.fieldGap,
  padding: '10px 14px',
  borderBottom: `1px solid ${theme.colors.borderLight}`,
  background: theme.colors.bgCard,

  '&[draggable="true"]': {
    cursor: 'grab',
  },
  '&.is-dragging': {
    opacity: 0.4,
  },
  '&.is-drop-target': {
    borderTop: `2px solid ${theme.colors.primary}`,
  },
  '&.is-locked': {
    background: theme.colors.bgMuted,
    color: theme.colors.textMuted,
  },
  '&.is-removed .sr-name, &.is-removed .sr-meta, &.is-removed .sr-totes': {
    textDecoration: 'line-through',
    color: theme.colors.textMuted,
  },
  '&.is-anchored': {
    background: theme.colors.cautionBg,
  },
  // Ghost row for a missed stop the supervisor has staged onto this route
  // from yesterday's Missed Stops Review. Distinct cream tint + amber `?`
  // badge so it's obvious the row isn't a real sequenced stop yet —
  // NextMV assigns the actual sequence position on re-optimize.
  '&.is-missed-stop-pending': {
    background: theme.colors.cautionBg,
  },

  '& .sr-drag': {
    color: theme.colors.textMuted,
    fontSize: theme.sizes.iconLg,
    userSelect: 'none',
    textAlign: 'center',
    lineHeight: theme.lineHeights.tight,
  },
  '& .sr-drag.is-disabled': {
    visibility: 'hidden',
  },
  // Long location ids (e.g. "IL_KIRKLAND_AND_ELLIS_CHICAGO__333_…") would
  // overflow into the Totes column at grid's default `min-width: auto`.
  // Reset to 0 so the cell can shrink and the text below ellipsis-truncates.
  '& .sr-identity': {
    minWidth: 0,
    overflow: 'hidden',
  },
  '& .sr-name': {
    fontSize: theme.typography.body.fontSize,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.semibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .sr-meta': {
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: theme.spacing.gapXs,
  },
  '& .sr-totes': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.medium,
  },
  '& .sr-status': {
    display: 'inline-flex',
    minWidth: 0,
  },
  // StopStatusPill defaults to `white-space: nowrap` so most labels (e.g.
  // "Stocked · 12:12 PM") stay on one line. The missed-stop label includes
  // the source route + date and is too long for the 150px Status column —
  // let it wrap here. The lock icon stays aligned to the first line.
  '& .sr-status > span': {
    whiteSpace: 'normal',
    alignItems: 'flex-start',
  },
  // Not-started rows render as a muted em-dash so the column is never blank
  // but stays visually quiet — only engaged stops get a colored pill.
  '& .sr-status-not-started': {
    color: theme.colors.textMuted,
    fontSize: theme.typography.hint.fontSize,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .sr-window': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.inputLabelGap,
  },
  '& .sr-window-field': {
    flex: 1,
    minWidth: 0,
    padding: '6px 8px',
    fontSize: theme.sizes.smallFontSize,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.input,
    background: theme.colors.bgCard,
    color: theme.colors.textDark,
    cursor: 'pointer',
    textAlign: 'left',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.labelGap,
    // Prevent the label from wrapping when the time string is wider than
    // the cell (e.g. "10:00 am" with the caret). Without this the cell
    // grows to two lines and the row becomes taller than its siblings.
    whiteSpace: 'nowrap',
  },
  '& .sr-window-field > span:first-of-type': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .sr-window-field:disabled': {
    cursor: 'not-allowed',
    background: theme.colors.bgMuted,
    color: theme.colors.textMuted,
  },
  '& .sr-window-field:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  // Single "Open 24h" pill replaces the two start/end cells when the
  // location's OpsOrder row marks it open 24h. Stretches to fill the full
  // window column so it aligns with the start/end labels in sibling rows.
  '& .sr-window-24h': {
    flex: '1 1 100%',
    fontWeight: theme.fontWeights.medium,
  },
  '& .sr-window-caret': {
    color: theme.colors.textMuted,
    fontSize: theme.typography.microLabel.fontSize,
  },
  '& .sr-window-sep': {
    color: theme.colors.textMuted,
    fontSize: theme.typography.body.fontSize,
  },
  '&.is-anchored .sr-window-field': {
    borderColor: theme.colors.warningText,
  },
  '& .sr-window-field.is-static': {
    cursor: 'default',
    border: 'none',
    background: 'transparent',
  },
  '& .sr-actions': {
    display: 'flex',
    gap: theme.spacing.inputLabelGap,
    justifyContent: 'flex-end',
  },
  '& .sr-action-btn': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.labelGap,
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.medium,
    border: '1px solid',
    borderRadius: theme.radii.input,
    background: theme.colors.bgCard,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  // Compact 28×28 square icon button so the Action column can shrink.
  // Default state is the × glyph; pending-removal swaps to ↶ at the same
  // size. The aria-label/title carries the verbal meaning for screen
  // readers and hover tooltips.
  '& .sr-action-delete': {
    color: theme.colors.danger,
    borderColor: theme.colors.danger,
    width: '28px',
    height: '28px',
    padding: 0,
    fontSize: theme.typography.body.fontSize,
    lineHeight: theme.lineHeights.tight,
  },
  '& .sr-action-delete:hover:not(:disabled)': {
    background: theme.colors.dangerHoverBg,
  },
  '& .sr-action-btn:disabled': {
    color: theme.colors.disabled,
    borderColor: theme.colors.border,
    cursor: 'not-allowed',
  },
});

function StopRow({
  stop,
  stopKey,
  positionLabel,
  pendingRemoval,
  bulkQueued,
  isAnchored,
  windowStart,
  windowEnd,
  isOpen24h,
  isDragging,
  isDropTarget,
  isLocked,
  isViewOnly,
  onToggleBulkQueue,
  onUndoRemove,
  onPinClick,
  onRequestDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) {
  const isMissedStopPending = Boolean(stop.isMissedStopPending);
  // Ghost rows are never actionable — they're a placeholder for a stop
  // NextMV will insert on the next re-optimize. Cancel on the route card
  // removes them entirely.
  const actionable =
    !isLocked && !isMissedStopPending && isStopActionable(stop);
  // Pin modal is only for *changing* an existing window. A "current" window
  // is either the original from the backend, the OpsOrders default, or one
  // already set by the supervisor via a previous pin. Stops with neither
  // stay un-clickable on the start/end cells; the Open 24h pill remains
  // clickable so supervisors can tighten an all-day window. Drag-drop opens
  // the modal regardless — that path expresses positional intent.
  const hasCurrentWindow = Boolean(windowStart || windowEnd);

  const classes = [
    isDragging ? 'is-dragging' : '',
    isDropTarget ? 'is-drop-target' : '',
    isLocked ? 'is-locked' : '',
    pendingRemoval ? 'is-removed' : '',
    isAnchored ? 'is-anchored' : '',
    isMissedStopPending ? 'is-missed-stop-pending' : '',
  ]
    .filter(Boolean)
    .join(' ');

  let badgeTone = 'success';
  if (isLocked || pendingRemoval) badgeTone = 'muted';
  else if (isMissedStopPending) badgeTone = 'pending';

  const primaryName =
    stop.displayName || stop.id || stop.stop || 'Unnamed stop';
  const technicalId = stop.id || '';
  const showTechnical = technicalId && technicalId !== primaryName;
  const toteCount =
    typeof stop.toteCountPerStop === 'number' ? stop.toteCountPerStop : null;

  // Status column content. NOT_STARTED renders as muted "— Not started" so
  // the column is never blank but stays visually quiet; everything else
  // gets a colored StopStatusPill with the lock icon. COMPLETED augments
  // the label with the stocked-at time when available.
  const derivedStatusId =
    (stop.derivedStatus && stop.derivedStatus.status) || STATUS_IDS.NOT_STARTED;
  const statusMeta =
    STATUS_META[derivedStatusId] || STATUS_META[STATUS_IDS.NOT_STARTED];
  const isNotStarted = derivedStatusId === STATUS_IDS.NOT_STARTED;
  let statusLabel = statusMeta.label;
  if (derivedStatusId === STATUS_IDS.COMPLETED) {
    const stockedAt =
      (stop.derivedStatus && stop.derivedStatus.stockedAt) ||
      formatStockedTime(stop.stockedTimeUTC);
    if (stockedAt) statusLabel = `Stocked · ${stockedAt}`;
  } else if (derivedStatusId === STATUS_IDS.MISSED_STOP_PENDING) {
    const fromRoute = stop.missedStopOriginalRoute;
    const fromDate = stop.missedStopOriginalDate;
    if (fromRoute && fromDate) {
      statusLabel = `Missed stop · from ${fromRoute} (${fromDate})`;
    } else if (fromRoute) {
      statusLabel = `Missed stop · from ${fromRoute}`;
    } else if (fromDate) {
      statusLabel = `Missed stop · from ${fromDate}`;
    }
  }

  return (
    <StyledDiv
      className={classes}
      draggable={actionable && !isViewOnly}
      onDragStart={actionable && !isViewOnly ? onDragStart : undefined}
      onDragEnd={actionable && !isViewOnly ? onDragEnd : undefined}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-anchored={isAnchored ? 'true' : undefined}
      data-testid={`stop-row-${stopKey}`}
    >
      {isViewOnly ? (
        <span aria-hidden="true" />
      ) : (
        <Checkbox
          checked={bulkQueued}
          onChange={onToggleBulkQueue}
          disabled={!actionable}
          aria-label={`Queue stop ${positionLabel} for bulk action`}
          dataTestId={`stop-row-bulk-checkbox-${stopKey}`}
        />
      )}
      <span
        className={`sr-drag${actionable ? '' : ' is-disabled'}`}
        aria-hidden="true"
      >
        {isViewOnly ? '' : '≡'}
      </span>
      <StopBadge
        tone={badgeTone}
        ariaLabel={
          isMissedStopPending
            ? 'Missed stop pending — NextMV will assign the position'
            : `Stop ${positionLabel}`
        }
        title={
          isMissedStopPending
            ? 'NextMV will assign the position when the route re-optimizes.'
            : undefined
        }
        dataTestId={`stop-row-badge-${stopKey}`}
      >
        {isMissedStopPending ? '?' : positionLabel}
      </StopBadge>
      <div className="sr-identity">
        <div className="sr-name" title={primaryName}>
          {primaryName}
        </div>
        {showTechnical && (
          <div className="sr-meta" title={technicalId}>
            {technicalId}
          </div>
        )}
      </div>
      <div className="sr-totes">
        {toteCount === null ? '—' : `${toteCount} totes`}
      </div>
      <div className="sr-status" data-testid={`stop-row-status-${stopKey}`}>
        {isNotStarted ? (
          <span
            className="sr-status-not-started"
            title={statusMeta.description}
          >
            — {statusLabel}
          </span>
        ) : (
          <StopStatusPill
            tone={statusMeta.tone}
            label={statusLabel}
            locked
            tooltip={statusMeta.description}
          />
        )}
      </div>
      <div className="sr-window">
        {isViewOnly ? (
          <>
            {isOpen24h ? (
              <span className="sr-window-field sr-window-24h is-static">
                <span>Open 24h</span>
              </span>
            ) : (
              <>
                <span className="sr-window-field is-static">
                  <span>{windowStart || '—'}</span>
                </span>
                <span className="sr-window-sep" aria-hidden="true">
                  –
                </span>
                <span className="sr-window-field is-static">
                  <span>{windowEnd || '—'}</span>
                </span>
              </>
            )}
          </>
        ) : (
          <>
            {isOpen24h ? (
              <button
                type="button"
                className="sr-window-field sr-window-24h"
                onClick={() => onPinClick(stop, stopKey)}
                disabled={!actionable}
                aria-label="Edit delivery window — location open 24 hours"
                title="Location is open 24 hours. Click to set a tighter delivery window."
                data-testid={`stop-row-window-24h-${stopKey}`}
              >
                <span>Open 24h</span>
                {actionable && (
                  <span className="sr-window-caret" aria-hidden="true">
                    ▾
                  </span>
                )}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="sr-window-field"
                  onClick={() => onPinClick(stop, stopKey)}
                  disabled={!actionable || !hasCurrentWindow}
                  aria-label="Edit delivery window start"
                  data-testid={`stop-row-window-start-${stopKey}`}
                >
                  <span>{windowStart || '—'}</span>
                  {hasCurrentWindow && (
                    <span className="sr-window-caret" aria-hidden="true">
                      ▾
                    </span>
                  )}
                </button>
                <span className="sr-window-sep" aria-hidden="true">
                  –
                </span>
                <button
                  type="button"
                  className="sr-window-field"
                  onClick={() => onPinClick(stop, stopKey)}
                  disabled={!actionable || !hasCurrentWindow}
                  aria-label="Edit delivery window end"
                  data-testid={`stop-row-window-end-${stopKey}`}
                >
                  <span>{windowEnd || '—'}</span>
                  {hasCurrentWindow && (
                    <span className="sr-window-caret" aria-hidden="true">
                      ▾
                    </span>
                  )}
                </button>
              </>
            )}
          </>
        )}
      </div>
      <div className="sr-actions">
        {isViewOnly ? (
          <span aria-hidden="true" />
        ) : (
          <button
            type="button"
            className="sr-action-btn sr-action-delete"
            onClick={() => {
              if (pendingRemoval) {
                onUndoRemove();
              } else {
                onRequestDelete(stop, stopKey);
              }
            }}
            disabled={!actionable}
            aria-pressed={pendingRemoval}
            aria-label={pendingRemoval ? 'Undo remove' : 'Remove this stop'}
            title={pendingRemoval ? 'Undo remove' : 'Remove this stop'}
            data-testid={`stop-row-delete-${stopKey}`}
          >
            {pendingRemoval ? '↶' : '×'}
          </button>
        )}
      </div>
    </StyledDiv>
  );
}

StopRow.propTypes = {
  stop: PropTypes.shape({
    id: PropTypes.string,
    // BE returns the sequence as a number; tests and missed-stop fixtures
    // sometimes pass strings — accept either rather than coercing at the
    // boundary.
    stop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    displayName: PropTypes.string,
    stockingIndex: PropTypes.number,
    status: PropTypes.string,
    stockedTimeUTC: PropTypes.string,
    toteCountPerStop: PropTypes.number,
    derivedStatus: PropTypes.shape({
      status: PropTypes.string,
      stockedAt: PropTypes.string,
      scannedAt: PropTypes.string,
      driver: PropTypes.string,
    }),
    // Ghost-row markers — only set on supervisor-staged missed stops.
    isMissedStopPending: PropTypes.bool,
    missedStopOriginalRoute: PropTypes.string,
    missedStopOriginalDate: PropTypes.string,
  }).isRequired,
  stopKey: PropTypes.string.isRequired,
  positionLabel: PropTypes.number.isRequired,
  pendingRemoval: PropTypes.bool,
  bulkQueued: PropTypes.bool,
  isAnchored: PropTypes.bool,
  windowStart: PropTypes.string,
  windowEnd: PropTypes.string,
  isOpen24h: PropTypes.bool,
  isDragging: PropTypes.bool,
  isDropTarget: PropTypes.bool,
  isLocked: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  onToggleBulkQueue: PropTypes.func.isRequired,
  onUndoRemove: PropTypes.func.isRequired,
  onPinClick: PropTypes.func.isRequired,
  onRequestDelete: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
};

StopRow.defaultProps = {
  pendingRemoval: false,
  bulkQueued: false,
  isAnchored: false,
  windowStart: '',
  windowEnd: '',
  isOpen24h: false,
  isDragging: false,
  isDropTarget: false,
  isLocked: false,
  isViewOnly: false,
};

export default StopRow;
